import { useState, useEffect, useRef } from 'react';
import { Badge } from '../../components/ui/badge';
import { TrendingDown, ChevronDown, ChevronUp } from 'lucide-react';

const riskColors = { Low: '#22c55e', Medium: '#d97706', High: '#ef4444', Critical: '#991b1b' };
const severityDot = { high: '#f59e0b', medium: '#f59e0b', low: '#cccccc' };
const impactStyles = {
  High: { bg: '#fef2f2', color: '#A32D2D' },
  Medium: { bg: '#fffbeb', color: '#854F0B' },
  Low: { bg: '#f0fdf4', color: '#3B6D11' },
};

/* ── Mock prediction data per claim ── */
const MOCK_PREDICTIONS = {
  'CLM-4921': {
    denial_probability: 4,
    risk_level: 'Low',
    risk_factors: [
      { factor: 'Modifier 25 on E/M with procedure', severity: 'medium', detail: 'Modifier 25 is present on 99214 billed same day as 36415. Some payers audit this combination.', impact: 'Medium' },
      { factor: 'Medical necessity documentation', severity: 'low', detail: 'E11.65 and I10 are well-established chronic conditions with clear clinical support for the visit level.', impact: 'Low' },
    ],
    recommendations: [
      'Ensure the chart note documents a separately identifiable E/M service when modifier 25 is used.',
      'Verify the assessment links each diagnosis to the plan of care for audit readiness.',
    ],
    payer_insight: 'Blue Cross has a 2% denial rate for 99214 with modifier 25. This is within acceptable range.',
  },
  'CLM-4922': {
    denial_probability: 38,
    risk_level: 'High',
    risk_factors: [
      { factor: 'Missing prior authorization', severity: 'high', detail: 'Aetna requires pre-auth for rapid strep (87880) after 3 tests per year. Patient has 2 YTD — approaching limit.', impact: 'High' },
      { factor: 'Pre-auth field empty', severity: 'high', detail: 'No pre-authorization number on file for Aetna. Claims without PA number may be auto-denied.', impact: 'High' },
      { factor: 'Rapid strep yearly limit', severity: 'medium', detail: '87880 approaching yearly limit of 3 for this payer. Current count: 2 YTD.', impact: 'Medium' },
    ],
    recommendations: [
      'Verify with Aetna if pre-auth is required for this specific test given the patient has 2 YTD.',
      'If pre-auth is required, obtain retro-authorization before submission.',
      'Document medical necessity for the rapid strep test in the chart note.',
    ],
    payer_insight: 'Aetna denies 12% of 87880 claims without prior auth. Appeal success rate for retro-auth: 34%.',
  },
  'CLM-4923': {
    denial_probability: 3,
    risk_level: 'Low',
    risk_factors: [
      { factor: 'Visit level appropriate', severity: 'low', detail: '99214 with trigger point injection (20552) is a common and well-supported combination for back pain.', impact: 'Low' },
    ],
    recommendations: [
      'No changes needed. Claim is clean and ready to submit.',
    ],
    payer_insight: 'UnitedHealth has a 1% denial rate for 99214 + 20552. No specific flags.',
  },
  'CLM-4924': {
    denial_probability: 11,
    risk_level: 'Medium',
    risk_factors: [
      { factor: 'High-level E/M with NCS', severity: 'medium', detail: '99215 billed with nerve conduction study (95907). Some payers require separate visit for diagnostic testing.', impact: 'Medium' },
      { factor: 'AI-suggested procedure', severity: 'low', detail: 'CPT 95907 was AI-suggested. Verify physician order exists in the chart.', impact: 'Low' },
    ],
    recommendations: [
      'Confirm the nerve conduction study was ordered and performed on the same visit.',
      'Ensure modifier 25 on 99215 is supported by documentation of a separately identifiable E/M service.',
    ],
    payer_insight: 'Cigna has a 5% denial rate for 99215 + diagnostic testing same day. Pre-auth PA-29481 is on file.',
  },
  'CLM-4925': {
    denial_probability: 72,
    risk_level: 'Critical',
    risk_factors: [
      { factor: 'Medicare preventive code mismatch', severity: 'high', detail: 'CPT 99395 is a preventive E/M code not covered by Medicare for patients over 65. Use AWV codes instead.', impact: 'High' },
      { factor: 'CPT/ICD mismatch', severity: 'high', detail: 'Z00.00 (general exam) paired with 99395 does not meet Medicare medical necessity requirements.', impact: 'High' },
      { factor: 'Low confidence extraction', severity: 'medium', detail: 'The CPT code was extracted with 72% confidence. Verify against the superbill.', impact: 'Medium' },
    ],
    recommendations: [
      'Change CPT 99395 to G0438 (initial AWV) or G0439 (subsequent AWV) for Medicare compliance.',
      'Update diagnosis code to Z00.00 with appropriate AWV diagnosis.',
      'Verify the CPT code against the original superbill — extraction confidence was low.',
    ],
    payer_insight: 'Medicare denies 95% of 99395 claims for patients over 65. AWV codes (G0438/G0439) are required.',
  },
  'CLM-4918': {
    denial_probability: 2,
    risk_level: 'Low',
    risk_factors: [
      { factor: 'Standard acute visit', severity: 'low', detail: 'Acute bronchitis (J20.9) with pulse oximetry (94760) is a clean, well-supported claim.', impact: 'Low' },
    ],
    recommendations: ['No changes needed. Claim is clean.'],
    payer_insight: 'Blue Cross has a <1% denial rate for this code combination.',
  },
  'CLM-4916': {
    denial_probability: 3,
    risk_level: 'Low',
    risk_factors: [
      { factor: 'UTI standard workup', severity: 'low', detail: 'Urinalysis (81003) + culture (87086) with E/M is a standard and well-accepted combination.', impact: 'Low' },
    ],
    recommendations: ['Claim is clean and ready for submission.'],
    payer_insight: 'Aetna has a 1% denial rate for UTI evaluation claims.',
  },
  'CLM-4914': {
    denial_probability: 5,
    risk_level: 'Low',
    risk_factors: [
      { factor: 'Joint injection with E/M', severity: 'low', detail: '99215 with knee injection (20610-RT) is standard. Modifier RT correctly applied.', impact: 'Low' },
    ],
    recommendations: ['No changes needed. Claim is well-documented.'],
    payer_insight: 'Cigna has a 2% denial rate for 99215 + 20610. No flags.',
  },
};

const getMockPrediction = (claim) => {
  if (MOCK_PREDICTIONS[claim?.id]) return MOCK_PREDICTIONS[claim.id];
  // Fallback for any claim not in the map
  return {
    denial_probability: 8,
    risk_level: 'Low',
    risk_factors: [
      { factor: 'Standard claim review', severity: 'low', detail: 'No significant denial risk factors detected for this claim.', impact: 'Low' },
    ],
    recommendations: ['Claim appears clean. Proceed with submission.'],
    payer_insight: `${claim?.payer || 'Payer'} has standard processing times for this code set.`,
  };
};

const DenialPredictionPanel = ({ claim }) => {
  const [prediction, setPrediction] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const prevClaimRef = useRef(null);

  const runPrediction = (toggle) => {
    if (toggle && prediction) { setExpanded(!expanded); return; }
    const mock = getMockPrediction(claim);
    setPrediction(mock);
    setExpanded(true);
  };

  // Auto-load mock prediction on claim change
  useEffect(() => {
    if (!claim?.id) return;
    if (claim.id === prevClaimRef.current) return;
    prevClaimRef.current = claim.id;
    const mock = getMockPrediction(claim);
    setPrediction(mock);
    setExpanded(true);
  }, [claim]);

  const p = prediction;

  return (
    <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: 12, overflow: 'hidden' }} data-testid="section-denial-prediction">
      {/* Header row — always visible */}
      <button onClick={() => runPrediction(true)} className="w-full flex items-center gap-2 text-left" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '12px 16px' }} data-testid="run-denial-prediction-btn">
        <TrendingDown style={{ width: 13, height: 13, color: '#ef4444' }} />
        <span style={{ fontSize: 12, fontWeight: 500 }}>AI denial prediction</span>
        <Badge style={{ fontSize: 9, border: 'none', background: '#f0f0f0', color: '#888', marginLeft: 2 }}>GPT-5.2</Badge>
        {p && (
          <div className="ml-auto flex items-center gap-2">
            <span style={{ fontSize: 11, fontWeight: 500, color: riskColors[p.risk_level] || '#888' }}>{p.denial_probability}% risk</span>
            {expanded ? <ChevronUp style={{ width: 12, height: 12, color: '#aaa' }} /> : <ChevronDown style={{ width: 12, height: 12, color: '#aaa' }} />}
          </div>
        )}
      </button>

      {/* Expanded body — two-column internal with fixed height + scroll */}
      {p && expanded && (
        <div className="flex" style={{ borderTop: '0.5px solid #f0f0f0', animation: 'fadeIn 0.3s ease', maxHeight: 420 }}>
          {/* LEFT: Score anchor — does NOT scroll */}
          <div style={{ width: 180, padding: 16, borderRight: '0.5px solid #f0f0f0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 36, fontWeight: 500, color: riskColors[p.risk_level] || '#888' }}>{p.denial_probability}%</span>
            <Badge style={{ fontSize: 10, border: '0.5px solid #fde68a', background: riskColors[p.risk_level] === '#22c55e' ? '#f0fdf4' : riskColors[p.risk_level] === '#d97706' ? '#fffbeb' : '#fef2f2', color: riskColors[p.risk_level] === '#22c55e' ? '#3B6D11' : riskColors[p.risk_level] === '#d97706' ? '#854F0B' : '#A32D2D', borderRadius: 20, padding: '2px 8px', marginTop: 6 }}>{p.risk_level} risk</Badge>
            <p style={{ fontSize: 10, color: '#aaa', marginTop: 6 }}>Confidence: {(p.confidence || 'medium').toLowerCase()}</p>
            <div style={{ width: '80%', height: 5, borderRadius: 3, background: '#f0f0f0', overflow: 'hidden', marginTop: 8 }}>
              <div style={{ height: '100%', width: `${p.denial_probability}%`, background: riskColors[p.risk_level] || '#888', borderRadius: 3, transition: 'width 0.5s ease' }} />
            </div>
          </div>

          {/* RIGHT: Details — scrollable */}
          <div style={{ flex: 1, maxHeight: 420, overflowY: 'auto', position: 'relative' }} className="denial-scroll-panel">
            <div style={{ padding: '14px 16px' }}>
              {/* Payer insight summary */}
              {p.payer_insight && (
                <p style={{ fontSize: 12, color: '#555', lineHeight: '1.6', marginBottom: 12 }}>{p.payer_insight}</p>
              )}

              {/* Risk factors */}
              {p.risk_factors?.length > 0 && (
                <>
                  <p style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 500, marginBottom: 7 }}>Risk factors</p>
                  <div>
                    {p.risk_factors.map((rf, i) => (
                      <div key={i} className="flex items-start gap-2" style={{ padding: '5px 0', borderBottom: '0.5px solid #f5f5f5' }}>
                        <div style={{ width: 5, height: 5, borderRadius: '50%', background: severityDot[rf.severity] || '#ccc', marginTop: 5, flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: 11, color: '#444', lineHeight: '1.4', fontWeight: 500 }}>{rf.factor}</span>
                          {rf.detail && <p style={{ fontSize: 10, color: '#888', lineHeight: '1.4', marginTop: 2 }}>{rf.detail}</p>}
                        </div>
                        <span style={{ fontSize: 9, background: impactStyles[rf.impact]?.bg || '#f0f0f0', color: impactStyles[rf.impact]?.color || '#888', borderRadius: 3, padding: '1px 5px', marginLeft: 'auto', flexShrink: 0 }}>{rf.impact}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Recommendations */}
              {p.recommendations?.length > 0 && (
                <>
                  <p style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 500, marginTop: 14, paddingTop: 12, borderTop: '0.5px solid #f0f0f0', marginBottom: 8 }}>Recommendations</p>
                  <div>
                    {p.recommendations.map((rec, i) => (
                      <div key={i} className="flex items-start gap-2" style={{ padding: '5px 0', borderBottom: '0.5px solid #f5f5f5' }}>
                        <span style={{ fontSize: 10, color: '#3b82f6', fontWeight: 600, flexShrink: 0, marginTop: 1 }}>{i + 1}.</span>
                        <p style={{ fontSize: 11, color: '#444', lineHeight: '1.5' }}>{rec}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            {/* Bottom fade gradient */}
            <div style={{
              position: 'sticky', bottom: 0, left: 0, right: 0, height: 24,
              background: 'linear-gradient(to bottom, transparent, white)',
              pointerEvents: 'none',
            }} />
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        .denial-scroll-panel::-webkit-scrollbar { width: 4px; }
        .denial-scroll-panel::-webkit-scrollbar-track { background: #f0f0f0; }
        .denial-scroll-panel::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 2px; }
      `}</style>
    </div>
  );
};

export default DenialPredictionPanel;
